import type { DatasetStorageBackend } from "@/api/datasetStorageBackend";
import { Text } from "@carbon6/c6-frontend-design";
import { useState, type ReactNode } from "react";
import type {
  Dataset,
  EpisodeListFilters,
  DatasetDetails,
  DatasetStructure,
  DatasetStructureChannel,
  MemoPreview,
} from "@/api";
import { hatcheryTextModes } from "@/app/theme";
import { EpisodesViewer, FileBrowser, datasetFileBrowserSource } from "@/components/dataset";
import {
  DatasetEpisodeAnnotationPanel,
  useDatasetEpisodeAnnotations,
} from "@/components/dataset/annotation";
import type { LoadState } from "@/components/dataset/loadState";
import {
  DatasetReviewPanel,
  type DatasetReviewOutcome,
  type DatasetReviewPanelProps,
} from "@/components/dataset/review/DatasetReviewPanel";
import {
  EpisodeDiagnosticsSurface,
  useCollectionDiagnostics,
  useEpisodeDiagnostics,
  type DiagnosticsViewId,
} from "@/components/dataset/diagnostics";
import { DatasetDownloadPanel } from "@/components/dataset/download";
import { MemoPopover } from "@/components/memo";
import styles from "./DatasetDetailBody.module.css";

// 본문은 탭을 바꿔도 같은 브라우저를 유지한다. 메타 정보 탭에서 Dataset 설정 전체를 다룬다.
// 리뷰 패널은 리뷰 탭에서만 표시한다. 구간 칩과 재생 바는 두 탭에서 유지한다.
// 진단 그래프는 분석 탭에서 표시한다. 리뷰 탭에서는 그리퍼만 펼칠 수 있다.
export type DatasetDetailBodyTab = "metadata" | "analysis" | "episodeReview" | "annotation";

export type DatasetDetailBodyProps = {
  model: DatasetDetailBodyModel;
  activeTab: DatasetDetailBodyTab;
  episodeFilters?: EpisodeListFilters;
};

// 페이지 controller 전체가 아니라 본문 렌더에 필요한 입력만 가진다. 페이지는 이 구조를
// 만족하는 모델을 전달한다. 공용 컴포넌트는 상위 page 계층의 구현 타입을 참조하지 않는다.
export type DatasetDetailBodyModel = {
  datasetUuid: string;
  dataset: LoadState<Dataset | null>;
  datasetDetails: LoadState<DatasetDetails>;
  structure: LoadState<DatasetStructure>;
  currentDataset: Dataset | null;
  review: DatasetReviewPanelProps["review"];
  onReviewOutcome: (outcome: DatasetReviewOutcome) => void;
  onDesignateVideoFps: (fps: number) => Promise<void>;
};

export function DatasetDetailBody({
  model,
  activeTab,
  episodeFilters,
}: DatasetDetailBodyProps): ReactNode {
  const isEpisodes = activeTab === "analysis" || activeTab === "episodeReview";
  // 에피소드 탭에서만 컬렉션 진단의 기준 밴드(p10–p90)를 1회 로드한다. 이 밴드를
  // "Parquet 미리보기" 위에 겹쳐 그린다. 개요 탭, 미산출, 실패이면 null 을 쓴다.
  const band = useCollectionDiagnostics(model.datasetUuid, activeTab === "analysis");
  // 큐레이션 지표도 같은 게이트에서 컬렉션당 1회 적재한다. 조회 경로에 에피소드 필터가 없으므로
  // 전체를 받아야 한다. 여기서 받으면 에피소드를 바꿔도 다시 받지 않는다.
  const episodeDiagnostics = useEpisodeDiagnostics(model.datasetUuid, activeTab === "analysis");
  // 채널 구조는 컨트롤러가 이미 받는다. 그룹, 표시명, 상대 프레임의 SoT 이므로 미리보기에 전달한다.
  const structureChannels =
    model.structure.status === "success" ? model.structure.data.channels : undefined;

  return (
    <section
      className={styles.page}
      aria-label="Dataset 상세"
      data-fill="true"
      hidden={activeTab === "annotation"}
    >
      {model.dataset.status === "error" ? (
        <Text variant={hatcheryTextModes["body-normal"]} className={styles.errorText} role="alert">
          {model.dataset.message}
        </Text>
      ) : null}
      {/* 다운로드 진행. 트리거(헤더 액션)와 형제 요소이므로 스토어로 연결한다.
          탭과 관계없이 같은 위치에 두어 탭을 바꿔도 진행 표시가 유지된다.
          페이지를 벗어나면 다운로드는 중단된다. */}
      <DatasetDownloadPanel datasetUuid={model.datasetUuid} />
      <div className={styles.retainedBrowser} hidden={!isEpisodes} inert={!isEpisodes}>
        <EpisodesTab
          mode={activeTab}
          active={isEpisodes}
          datasetUuid={model.datasetUuid}
          episodeFilters={episodeFilters}
          memoCount={model.currentDataset?.memo_count ?? 0}
          latestMemo={model.currentDataset?.latest_memo ?? null}
          canModifyExtraFiles={model.currentDataset?.status === "available"}
          storageBackend={model.currentDataset?.storage_backend}
          band={band}
          episodeDiagnostics={episodeDiagnostics}
          structureChannels={structureChannels}
        />
      </div>
      {activeTab === "metadata" ? (
        // 쓰기 가능한 항목은 패널이 설정 응답의 permissions 로 정한다.
        <DatasetReviewPanel
          review={model.review}
          // 메타데이터·에피소드 품질은 설정과 다른 소스이므로 상세 응답을 그대로 전달한다.
          details={model.datasetDetails}
          onOutcome={model.onReviewOutcome}
          // fps 지정은 설정 확정과 다른 엔드포인트이므로 draft 를 거치지 않는다. 호출, 안내,
          // 재조회는 컨트롤러가 소유한다. 카드는 값만 전달한다.
          onDesignateVideoFps={model.onDesignateVideoFps}
          dataset={model.currentDataset}
          structureState={model.structure}
          // 표본 레인. 확정해야 할 축의 절반(환경·작업·품질)은 영상을 봐야 판정할 수 있다.
          // 이 레인이 있으면 편집 중인 draft 를 유지한 채 이 탭에서 영상을 볼 수 있다.
          // 선택은 이 레인의 로컬 상태이며 에피소드/파일 탭과 공유하지 않는다. 두 탭이 같은
          // 커서를 쓰려면 선택을 창 스코프로 옮겨야 한다. 그것은 이 레인과 별개의 결정이다.
          sample={
            <EpisodesViewer
              source={datasetFileBrowserSource}
              resourceUuid={model.datasetUuid}
              mode="compact"
            />
          }
        />
      ) : null}
    </section>
  );
}

/** 선택, 재생, 리뷰 초안을 유지하는 공용 에피소드 브라우저. */
function EpisodesTab({
  mode,
  active,
  datasetUuid,
  memoCount,
  latestMemo,
  canModifyExtraFiles,
  storageBackend,
  episodeFilters,
  band,
  episodeDiagnostics,
  structureChannels,
}: {
  mode: DatasetDetailBodyTab;
  active: boolean;
  datasetUuid: string;
  memoCount: number;
  latestMemo: MemoPreview | null;
  canModifyExtraFiles: boolean;
  storageBackend: DatasetStorageBackend | undefined;
  episodeFilters?: EpisodeListFilters;
  band: ReturnType<typeof useCollectionDiagnostics>;
  episodeDiagnostics: ReturnType<typeof useEpisodeDiagnostics>;
  structureChannels: readonly DatasetStructureChannel[] | undefined;
}): ReactNode {
  // 선택된 에피소드의 인라인 리뷰 필드를 쓴다.
  const annotations = useDatasetEpisodeAnnotations(datasetUuid, true);
  // 영상 아래 진단 보기. 이 창의 로컬 상태이므로 에피소드를 바꿔도 유지되고 URL 에는 두지 않는다.
  // 창을 두 개 열면 창마다 따로 고른다. null 이면 표면의 기본 보기를 쓴다.
  const [diagnosticsView, setDiagnosticsView] = useState<DiagnosticsViewId | null>("motion");

  return (
    <section className={styles.browserSection} aria-label="에피소드/파일">
      <div className={styles.browserFill}>
        <FileBrowser
          source={datasetFileBrowserSource}
          datasetUuid={datasetUuid}
          canModify={canModifyExtraFiles}
          storageBackend={storageBackend}
          initialEpisodeFilters={episodeFilters}
          episodeSidebarHeader={
            <MemoPopover
              datasetUuid={datasetUuid}
              count={memoCount}
              latest={latestMemo}
              trigger="label"
              size="sm"
            />
          }
          mapEpisode={annotations.rowForEpisode}
          flashEpisodeUuid={annotations.savedFlashUuid}
          // 진단 표면은 두 탭에 모두 마운트된다. 구간·후보 칩과 재생 바 행의 유일한 소유자이기 때문이다.
          // 상세는 두 탭 모두 받는다. action_source 채널이 없는 Dataset 에도 영상 정지 후보가 있을 수
          // 있다. 진단 표면은 분석 그래프와 리뷰 그리퍼의 표시 및 데이터 로딩을 제어한다.
          renderEpisodeExtra={(episode, playback) => (
            <EpisodeDiagnosticsSurface
              enabled={active}
              bodyEnabled={active && mode === "analysis"}
              episode={episode}
              band={band}
              episodeDiagnostics={episodeDiagnostics}
              structureChannels={structureChannels}
              playback={playback}
              view={diagnosticsView}
              onViewChange={setDiagnosticsView}
            />
          )}
          // 영상 크기는 탭이 아니라 배치가 정한다. 분석에서는 그래프가 폭을 차지하고, 리뷰에서는
          // 영상이 본문을 채운다(`EpisodeVideoPreview` 의 fit 규칙).
          analysisLayout={mode === "analysis"}
          filesEnabled={mode !== "analysis"}
          // 패널 인스턴스를 유지해 탭 전환 중 초안과 이탈 확인을 보존한다.
          renderEpisodeFooter={(episode, nav) => (
            <DatasetEpisodeAnnotationPanel
              key={`${datasetUuid}:${episode.uuid}`}
              active={active && mode === "episodeReview"}
              datasetUuid={datasetUuid}
              episodeUuid={episode.uuid}
              activeRow={episode}
              options={annotations.options}
              loading={annotations.loading}
              trainingLocked={annotations.trainingLocked}
              onSaved={annotations.applySaved}
              nav={nav}
            />
          )}
        />
      </div>
    </section>
  );
}
